import React, { useEffect, useState, useMemo } from 'react';
import { useData } from '../../hooks/useData';
import { useToast } from '../../hooks/useToast';
import { EmptyState } from '../common/EmptyState';
import { MetricStrip } from '../common/MetricStrip';
import { Tag } from '../common/Tag';
import { nowISO } from '../../utils/helpers';
import KnowledgeUploadModal from './KnowledgeUploadModal';
import { knowledgeLibraryServices } from '../../APIServices/knowledgeLibraryServices';
import { knowledgeBaseMetadataServices } from '../../APIServices/knowledgeBaseMetadataServices';

function KnowledgeLibraryModule() {
  const { state, loadKnowledgeList, audit } = useData();
  const toast = useToast();
  const [showUpload, setShowUpload] = useState(false);
  const [collection, setCollection] = useState('all');
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [collectionMetadata, setCollectionMetadata] = useState([]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await loadKnowledgeList();
      const metadata = await knowledgeBaseMetadataServices.getKnowledgeBaseMetadataData(
        'active',
        'knowledgebase_collection'
      );
      setCollectionMetadata(metadata?.data || []);
      setLoading(false);
    };
    load();
  }, [loadKnowledgeList]);

  const approve = async (item) => {
    await knowledgeLibraryServices.updateKnowledgeLibraryItem({
      id: item._id,
      status: 'approved',
      approvedAt: nowISO(),
      approvedBy: state.currentUserId,
    });
    await loadKnowledgeList();
    audit('KNOWLEDGE_APPROVE', item.title, 'Approved for engine use', { knowledgeId: item.id });
    toast(`"${item.title}" approved · now searchable`, 'ok');
  };

  const getCollectionIcon = (value) => {
    if (value === 'literature') return 'microscope';
    if (value === 'regulatory') return 'scale';
    if (value === 'product') return 'pill';
    if (value === 'historical') return 'archive';
    return 'books';
  };

  const filtered = useMemo(() => {
    return state.knowledge.filter((k) => {
      if (k.deleted) return false;
      if (collection !== 'all' && k.collection !== collection) return false;
      if (statusFilter !== 'all' && k.status !== statusFilter) return false;
      if (!filter) return true;
      const q = filter.toLowerCase();
      return k.title.toLowerCase().includes(q) ||
        (k.molecule || '').toLowerCase().includes(q) ||
        (k.tags || []).join(' ').toLowerCase().includes(q);
    });
  }, [state.knowledge, collection, filter, statusFilter]);

  const counts = useMemo(() => {
    const total = state.knowledge.filter((k) => !k.deleted).length;
    const approved = state.knowledge.filter((k) => k.status === 'approved' && !k.deleted).length;
    const review = state.knowledge.filter((k) => k.status === 'review' && !k.deleted).length;
    return { total, approved, review };
  }, [state.knowledge]);

  const collections = useMemo(() => {
    const getIcon = (value) => {
      if (value === 'literature') return 'ti-microscope';
      if (value === 'regulatory') return 'ti-scale';
      if (value === 'product') return 'ti-pill';
      if (value === 'historical') return 'ti-archive';
      return 'ti-books';
    };

    const defaults = [
      { label: 'Scientific literature', value: 'literature' },
      { label: 'Regulatory guidance', value: 'regulatory' },
      { label: 'Product / drug', value: 'product' },
      { label: 'Arcolab historical', value: 'historical' },
    ];

    const rows = (collectionMetadata || []).length
      ? collectionMetadata.map((item) => ({ label: item.label, value: item.value || item.label }))
      : defaults;

    return [
      { id: 'all', name: 'All collections', icon: 'ti-books' },
      ...rows.map((item) => ({
        id: item.value,
        name: item.label,
        icon: getIcon(item.value),
      })),
    ];
  }, [collectionMetadata]);

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-eyebrow">Section 15 · Deterministic intelligence</div>
          <h1 className="page-title">Knowledge library</h1>
          <p className="page-sub">Controlled literature & evidence repository. Items become searchable to the deterministic engine only after approval.</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowUpload(true)}>
          <i className="ti ti-upload" /> Upload knowledge
        </button>
      </div>

      <MetricStrip
        items={[
          { label: 'Approved items', value: counts.approved, foot: 'available to engine' },
          { label: 'Pending approval', value: counts.review, dir: 'warn', foot: counts.review > 0 ? 'awaiting review' : 'none pending' },
          { label: 'Total indexed', value: counts.total, foot: 'all collections' },
          { label: 'Engine version', value: 'v2.3.1', foot: 'Rule pack v14 · Template v8' }
        ]}
      />

      <div className="section-label mt-4 mb-5">Browse by collection</div>

      <div className="split">
        <div className="split-left">
          <div className="tree">
            {collections.map((c) => (
              <div
                key={c.id}
                className={`tree-node ${collection === c.id ? 'active' : ''}`}
                onClick={() => setCollection(c.id)}
              >
                <i className={`ti ${c.icon}`} />
                <span>{c.name}</span>
                <span className="count">
                  {c.id === 'all' ? counts.total : state.knowledge.filter((k) => k.collection === c.id && !k.deleted).length}
                </span>
              </div>
            ))}
          </div>

          <div className="divider" />
          <div className="card-title">Engine governance</div>
          <div className="xs">All items pass through:</div>
          <div className="timeline mt-2" style={{ fontSize: 11 }}>
            <div className="timeline-item"><div className="timeline-title xs">Upload + metadata</div></div>
            <div className="timeline-item"><div className="timeline-title xs">Validate + OCR</div></div>
            <div className="timeline-item"><div className="timeline-title xs">Index full-text</div></div>
            <div className="timeline-item"><div className="timeline-title xs">Owner review</div></div>
            <div className="timeline-item accent"><div className="timeline-title xs">Approve for engine</div></div>
            <div className="timeline-item"><div className="timeline-title xs">Audit logged</div></div>
          </div>
          <div className="xs muted mt-3" style={{ borderTop: '1px solid var(--line)', paddingTop: 10 }}>
            <i className="ti ti-lock" style={{ color: 'var(--ok)' }} /> Processing on private environment. No external API calls.
          </div>
        </div>

        <div className="split-right">
          <div className="flex gap-2 mb-3">
            <div style={{ position: 'relative', flex: 1 }}>
              <i className="ti ti-search" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--ink-3)', fontSize: 13 }} />
              <input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Search title, molecule, tags..."
                style={{ width: '100%', padding: '7px 10px 7px 30px', border: '1px solid var(--line)', borderRadius: 2, fontSize: 12 }}
              />
            </div>
            <select className="btn btn-sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All statuses</option>
              <option value="approved">Approved</option>
              <option value="review">Under review</option>
            </select>
          </div>

          {loading ? (
            <EmptyState icon="ti-loader" title="Loading knowledge" desc="Fetching knowledge items from API..." />
          ) : filtered.length === 0 ? (
            <EmptyState icon="ti-books-off" title="No knowledge items" desc="Adjust filter or upload new literature." />
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Source</th>
                  <th>Year</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((k) => (
                  <tr key={k.id}>
                    <td>
                      <div className="flex gap-2 center">
                        <i className={`ti ti-${getCollectionIcon(k.collection)}`} style={{ fontSize: 15, color: 'var(--ink-3)' }} />
                        <div>
                          <div className="medium" style={{ maxWidth: 380, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {k.title}
                          </div>
                          <div className="xs muted">
                            {k.type} {k.molecule && `· ${k.molecule}`} · {k.pages || '—'} pages
                            {(k.tags || []).slice(0, 3).map((t) => (
                              <span key={t} style={{ marginLeft: 6, color: 'var(--accent)' }}>#{t}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="xs">{k.source || '—'}</td>
                    <td className="num xs">{k.year || '—'}</td>
                    <td>
                      {k.status === 'approved' ? (
                        <Tag variant="ok" dot>Approved</Tag>
                      ) : k.status === 'review' ? (
                        <Tag variant="warn" dot>Review</Tag>
                      ) : (
                        <Tag dot>{k.status}</Tag>
                      )}
                    </td>
                    <td>
                      {k.status === 'review' && (
                        <button className="btn btn-xs" onClick={() => approve(k)}>
                          <i className="ti ti-check" /> Approve
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <KnowledgeUploadModal open={showUpload} onClose={() => setShowUpload(false)} />
    </div>
  );
}

export default KnowledgeLibraryModule;